import { StateFields, Progress, INITIAL_PAGE } from '~/shared/utils/embroidery';

const initialState = {
  [StateFields.TEXT_CUSTOMIZATIONS]: {
    [StateFields.LINE_1]: '',
    [StateFields.LINE_2]: '',
    [StateFields.COLOR]: '',
    [StateFields.FONT]: '',
    [StateFields.TEXT_LOCATION]: '',
  },
  [StateFields.ICON_CUSTOMIZATIONS]: {
    [StateFields.ICON]: null,
    [StateFields.ICON_LOCATION]: '',
  },
  [StateFields.PREVIEW_IMAGES]: {},
  [StateFields.PREVIEW_LOADING]: false,
  [StateFields.PREVIEW_ERROR]: false,
  [StateFields.CURRENT_IDX]: INITIAL_PAGE,
};

const ACTION_PREFIX = 'embroidery';

const ActionTypes = {
  HANDLE_COMPLETE_VALUE_SUBMIT: `${ACTION_PREFIX}/submitValues`,
  HANDLE_PREVIEW_IMAGE_REQUEST: `${ACTION_PREFIX}/previewImageRequest`,
  HANDLE_PREVIEW_IMAGE_RESPONSE: `${ACTION_PREFIX}/previewImageResponse`,
  HANDLE_PROGRESS: `${ACTION_PREFIX}/progress`,
};

const createAction = (actionType, payload) => ({
  type: actionType,
  payload,
});

export const actions = {
  handleEmbroideryConfigSubmit: (payload) =>
    createAction(ActionTypes.HANDLE_COMPLETE_VALUE_SUBMIT, payload),
  handlePreviewImageRequest: () =>
    createAction(ActionTypes.HANDLE_PREVIEW_IMAGE_REQUEST),
  handlePreviewImageResponse: (payload) =>
    createAction(ActionTypes.HANDLE_PREVIEW_IMAGE_RESPONSE, payload),
  handleProgress: (payload) =>
    createAction(ActionTypes.HANDLE_PROGRESS, payload),
};

export const reducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_COMPLETE_VALUE_SUBMIT: {
      return {
        ...state,
        ...action.payload,
        [StateFields.PREVIEW_LOADING]: false,
        [StateFields.PREVIEW_ERROR]: false,
        [StateFields.CURRENT_IDX]: state[StateFields.CURRENT_IDX] + 1,
      };
    }
    case ActionTypes.HANDLE_PREVIEW_IMAGE_REQUEST:
      return {
        ...state,
        [StateFields.PREVIEW_LOADING]: true,
        [StateFields.PREVIEW_ERROR]: false,
      };
    case ActionTypes.HANDLE_PREVIEW_IMAGE_RESPONSE: {
      if (action.payload.error) {
        return {
          ...state,
          [StateFields.PREVIEW_LOADING]: false,
          [StateFields.PREVIEW_ERROR]: true,
        };
      } else {
        const imageCache = state[StateFields.PREVIEW_IMAGES];
        const cacheSize = Object.keys(imageCache).length;

        const updatedImages =
          cacheSize >= 20
            ? {
                // evict every value from the cache
                [action.payload.previewKey]: action.payload.previewImageSrc,
              }
            : {
                ...imageCache,
                [action.payload.previewKey]: action.payload.previewImageSrc,
              };

        return {
          ...state,
          [StateFields.PREVIEW_LOADING]: false,
          [StateFields.PREVIEW_IMAGES]: updatedImages,
        };
      }
    }
    case ActionTypes.HANDLE_PROGRESS: {
      const currentStep = state[StateFields.CURRENT_IDX];

      if (action.payload === Progress.CONTINUE) {
        return {
          ...state,
          [StateFields.CURRENT_IDX]: currentStep + 1,
        };
      }

      if (action.payload === Progress.BACK) {
        return {
          ...state,
          [StateFields.CURRENT_IDX]: currentStep - 1,
        };
      }

      if (action.payload === Progress.RESET) {
        // When resetting state we need to reset it back to what was returned
        // from the initState fn. The modal doesn't unmount when it closes so initState
        // won't run again. We default here to current state values for customization location.
        const currentIconCustomizations =
          state[StateFields.ICON_CUSTOMIZATIONS];
        const currentTextCustomizations =
          state[StateFields.TEXT_CUSTOMIZATIONS];

        return {
          ...initialState,
          [StateFields.ICON_CUSTOMIZATIONS]: {
            ...initialState[StateFields.ICON_CUSTOMIZATIONS],
            [StateFields.ICON_LOCATION]:
              currentIconCustomizations[StateFields.ICON_LOCATION],
          },
          [StateFields.TEXT_CUSTOMIZATIONS]: {
            ...initialState[StateFields.TEXT_CUSTOMIZATIONS],
            [StateFields.TEXT_LOCATION]:
              currentTextCustomizations[StateFields.TEXT_LOCATION],
          },
        };
      }

      break;
    }
    default:
      return state;
  }
};

export const initState = ({ textPersonalizations, iconPersonalizations }) => {
  const defaultIconLocation = iconPersonalizations?.locations[0]?.locationId;
  const defaultTextLocation = textPersonalizations?.locations[0]?.locationId;

  return {
    ...initialState,
    [StateFields.ICON_CUSTOMIZATIONS]: {
      ...initialState[StateFields.ICON_CUSTOMIZATIONS],
      [StateFields.ICON_LOCATION]: defaultIconLocation,
    },
    [StateFields.TEXT_CUSTOMIZATIONS]: {
      ...initialState[StateFields.TEXT_CUSTOMIZATIONS],
      [StateFields.TEXT_LOCATION]: defaultTextLocation,
    },
  };
};
